import { useCallback, useRef, useState } from "react";
import {
  AdvisorMessage,
  sendMessage as apiSendMessage,
} from "../../services/advisorService";

const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const WELCOME_MESSAGE: AdvisorMessage = {
  id: "welcome",
  role: "assistant",
  content:
    "Hello! I'm your Everpesa financial advisor. I can help you with investment opportunities, loan products, understanding risk, and managing your wallet. What can I help you with today?",
  timestamp: new Date(),
};

export const SUGGESTED_PROMPTS = [
  "What unit trust funds are available in Uganda?",
  "How can I invest on Everpesa?",
  "Tell me about loan products",
  "What are the risks of investing?",
  "How do I deposit money?",
  "Explain the fees",
];

export const useAdvisorChat = () => {
  const [messages, setMessages] = useState<AdvisorMessage[]>([WELCOME_MESSAGE]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const send = useCallback(
    async (text: string) => {
      if (!text.trim() || loading) return;

      setError(null);

      // Add user message
      const userMsg: AdvisorMessage = {
        id: generateId(),
        role: "user",
        content: text.trim(),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setLoading(true);

      try {
        const response = await apiSendMessage(text, [
          ...messages,
          userMsg,
        ]);

        const assistantMsg: AdvisorMessage = {
          id: generateId(),
          role: "assistant",
          content: response.content,
          timestamp: new Date(),
          metadata: response.metadata,
        };

        setMessages((prev) => [...prev, assistantMsg]);
      } catch (err: any) {
        setError("Failed to get a response. Please try again.");

        const errorMsg: AdvisorMessage = {
          id: generateId(),
          role: "assistant",
          content:
            "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setLoading(false);
      }
    },
    [messages, loading]
  );

  const clearChat = useCallback(() => {
    setMessages([WELCOME_MESSAGE]);
    setError(null);
  }, []);

  return {
    messages,
    loading,
    error,
    send,
    clearChat,
  };
};